"use client";

import type { GamePrioritiesResponse } from "@/app/_lib/apis/gamePriorities";
import type { GameWithQuests } from "@/components/QuestsV2/types";
import { Crown, HelpCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { GameQuestSearchDialog } from "./GameQuestSearchDialog";

interface QuestSearchBannerProps {
  /** Optional featured game thumbnails to show on the right */
  featuredThumbnails?: Array<{
    src: string;
    alt: string;
    href: string;
  }>;
  gamesWithQuests: GameWithQuests[];
  gamePriorities: GamePrioritiesResponse | null;
}

export function QuestSearchBanner({
  featuredThumbnails = [],
  gamesWithQuests,
  gamePriorities,
}: QuestSearchBannerProps) {
  return (
    <div className="w-full">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-neutral-900/80 via-neutral-900/60 to-neutral-900/40 backdrop-blur-xl border border-white/10">
        {/* Background gradient accent */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-purple-500/5 pointer-events-none" />

        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6 p-4 md:p-5">
          {/* Left side - Messaging */}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-bold text-white mb-1">
              Do Quests. $60K Weekly Prize Pool.
            </h2>
            <p className="text-sm text-white/60 flex items-center gap-1.5 flex-wrap">
              <span>The more Weekly Points</span>
              <Crown className="w-4 h-4 text-amber-400 inline" />
              <span>you earn, the higher your chances!</span>
              <Link
                href="/rewards/faq/"
                className="text-white/60 hover:text-white transition-colors ml-1"
                title="FAQ"
              >
                <HelpCircle className="w-4 h-4" />
              </Link>
            </p>
          </div>

          {/* Right side - Search + Thumbnails */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            {/* Search Button */}
            <GameQuestSearchDialog
              gamesWithQuests={gamesWithQuests}
              gamePriorities={gamePriorities}
            />

            {/* Featured Game Thumbnails - Desktop only */}
            {featuredThumbnails.length > 0 && (
              <div className="hidden lg:flex items-center gap-2">
                {featuredThumbnails.slice(0, 2).map((thumb) => (
                  <Link
                    key={thumb.href}
                    href={thumb.href}
                    className="shrink-0 rounded-lg overflow-hidden border-2 border-white/20 hover:border-white/40 transition-colors"
                  >
                    <Image
                      src={thumb.src}
                      alt={thumb.alt}
                      width={80}
                      height={60}
                      className="object-cover"
                    />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
